import { useState, useEffect } from 'react';
import type { Venue } from '../types';

interface VenueFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  venue?: Venue | null;
  tenants: Array<{ id: string; name: string }>;
}

export default function VenueFormModal({ isOpen, onClose, onSubmit, venue, tenants }: VenueFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    tenantId: '',
    province: '',
    city: '',
    district: '',
    detail: '',
    description: '',
    area: '',
    status: 'enabled' as 'enabled' | 'disabled',
    enabledTime: ''
  });

  const provinces = ['北京市', '上海市', '广东省', '浙江省', '四川省'];
  const cities: Record<string, string[]> = {
    '北京市': ['北京市'],
    '上海市': ['上海市'],
    '广东省': ['深圳市', '广州市'],
    '浙江省': ['杭州市'],
    '四川省': ['成都市']
  };
  const districts: Record<string, string[]> = {
    '北京市': ['朝阳区', '海淀区', '东城区', '西城区'],
    '上海市': ['浦东新区', '黄浦区', '徐汇区', '静安区'],
    '深圳市': ['南山区', '福田区', '罗湖区', '宝安区'],
    '广州市': ['天河区', '越秀区', '海珠区', '番禺区'],
    '杭州市': ['西湖区', '上城区', '拱墅区', '滨江区'],
    '成都市': ['高新区', '武侯区', '锦江区', '青羊区']
  };

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name,
        tenantId: venue.tenantId,
        province: venue.address.province,
        city: venue.address.city,
        district: venue.address.district,
        detail: venue.address.detail,
        description: venue.description || '',
        area: venue.area.toString(),
        status: venue.status,
        enabledTime: venue.enabledTime || ''
      });
    } else {
      setFormData({
        name: '',
        tenantId: '',
        province: '',
        city: '',
        district: '',
        detail: '',
        description: '',
        area: '',
        status: 'enabled',
        enabledTime: ''
      });
    }
  }, [venue, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('请输入场所名称');
      return;
    }
    if (!formData.tenantId) {
      alert('请选择租户');
      return;
    }
    if (!formData.province || !formData.city || !formData.district) {
      alert('请选择完整地址');
      return;
    }
    if (!formData.detail.trim()) {
      alert('请输入详细地址');
      return;
    }
    if (!formData.area || parseFloat(formData.area) <= 0) {
      alert('请输入有效的面积');
      return;
    }
    if (formData.status === 'enabled' && !formData.enabledTime) {
      alert('启用状态需要填写启用时间');
      return;
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {venue ? '编辑场所' : '新建场所'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                场所名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入场所名称"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                租户名称 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.tenantId}
                onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">请选择租户</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  省份 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.province}
                  onChange={(e) => {
                    setFormData({ ...formData, province: e.target.value, city: '', district: '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">请选择省份</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  城市 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.city}
                  onChange={(e) => {
                    setFormData({ ...formData, city: e.target.value, district: '' });
                  }}
                  disabled={!formData.province}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                >
                  <option value="">请选择城市</option>
                  {formData.province && cities[formData.province]?.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  区县 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  disabled={!formData.city}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                >
                  <option value="">请选择区县</option>
                  {formData.city && districts[formData.city]?.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                详细地址 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.detail}
                onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                placeholder="请输入详细地址"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入场所描述"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                面积（平米） <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                placeholder="请输入面积"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                状态 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="enabled"
                    checked={formData.status === 'enabled'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'enabled' | 'disabled' })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">启用</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="disabled"
                    checked={formData.status === 'disabled'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'enabled' | 'disabled' })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">停用</span>
                </label>
              </div>
            </div>

            {formData.status === 'enabled' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  启用时间 <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.enabledTime}
                  onChange={(e) => setFormData({ ...formData, enabledTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              确定
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
